import { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { API_BASE_URL, CDN_BASE_URL, PLACE_IMAGE_CACHE_BUST } from '../config';
import { getSubredditFromUrl, marketingHomeUrl } from '../utils/subreddit';
import { formatSubredditPlaceTitle, placeTeamPath, placeLeaderboardPath, placeTimelapsePath } from '../utils/placePaths';
import { placeLivePlacePngUrlWithBust } from '../utils/placeCdnUrls';

type FlairRow = { flairCode: string; flairAbbreviated: string; cdnStem: string };

export default function PlaceBrowsePage() {
  const { subreddit: subParam } = useParams<{ subreddit?: string }>();
  const subreddit = (subParam || getSubredditFromUrl() || 'cfb').toLowerCase();
  const titleSub = formatSubredditPlaceTitle(subreddit);

  const [flairs, setFlairs] = useState<FlairRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [placeFrozen, setPlaceFrozen] = useState(false);
  const [cdnCacheBust, setCdnCacheBust] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_BASE_URL}/place/${subreddit}/flairs`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || res.statusText);
        if (!cancelled) {
          setFlairs(data.flairs || []);
          setPlaceFrozen(Boolean(data.placeFrozen));
          setCdnCacheBust(data.cdnCacheBust != null ? String(data.cdnCacheBust) : null);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [subreddit]);

  const filtered = flairs.filter((f) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return (
      f.flairCode.toLowerCase().includes(s) ||
      f.flairAbbreviated.toLowerCase().includes(s)
    );
  });

  const backPath = subreddit === 'cfb' && getSubredditFromUrl() ? '/' : `/${subreddit}`;
  const bust = (cdnCacheBust ?? PLACE_IMAGE_CACHE_BUST) || undefined;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100', pb: 4 }}>
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar variant="dense">
          <IconButton edge="start" component={RouterLink} to={backPath} size="small" aria-label="Back">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ ml: 1, flex: 1, fontWeight: 600 }}>
            /r/{titleSub} Place — browse teams
          </Typography>
          <Button size="small" component={RouterLink} to={placeLeaderboardPath(subreddit)} sx={{ flexShrink: 0 }}>
            Leaderboard
          </Button>
          <Button size="small" component={RouterLink} to={placeTimelapsePath(subreddit)} sx={{ flexShrink: 0 }}>
            Timelapse
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ maxWidth: 960, mx: 'auto', px: 2, pt: 2 }}>
        {!placeFrozen && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Open any D1 team canvas.{' '}
            <RouterLink to={placeLeaderboardPath(subreddit)}>Pixel leaderboard</RouterLink>
            {' · '}
            <ButtonLink href={marketingHomeUrl()} label="FlairWizard home" />
          </Typography>
        )}

        {placeFrozen && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Place is closed, thank you for participating!
          </Alert>
        )}

        <TextField
          fullWidth
          size="small"
          placeholder="Filter by flair code or abbreviation…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                sm: 'repeat(3, 1fr)',
                md: 'repeat(4, 1fr)',
              },
              gap: 1.5,
            }}
          >
            {filtered.map((f) => (
              <Card key={f.flairCode} variant="outlined" sx={{ height: '100%' }}>
                <CardActionArea component={RouterLink} to={placeTeamPath(subreddit, f.flairCode)}>
                  <PlaceBrowseThumb subreddit={subreddit} cdnStem={f.cdnStem} cacheBust={bust} />
                  <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                    <Typography variant="body2" fontWeight={600} noWrap title={f.flairAbbreviated}>
                      {f.flairAbbreviated}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        )}

        {!loading && !error && filtered.length === 0 && (
          <Typography color="text.secondary">No teams match your filter.</Typography>
        )}
      </Box>
    </Box>
  );
}

function PlaceBrowseThumb({
  subreddit,
  cdnStem,
  cacheBust,
}: {
  subreddit: string;
  cdnStem: string;
  cacheBust?: string;
}) {
  const src = placeLivePlacePngUrlWithBust(CDN_BASE_URL, subreddit, cdnStem, cacheBust);
  return (
    <CardMedia
      component="img"
      sx={{
        height: 72,
        objectFit: 'contain',
        bgcolor: 'grey.100',
        imageRendering: 'pixelated',
      }}
      src={src}
      alt=""
      loading="lazy"
    />
  );
}

function ButtonLink({ href, label }: { href: string; label: string }) {
  return (
    <Typography
      component="a"
      href={href}
      variant="body2"
      sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
    >
      {label}
    </Typography>
  );
}
